import { useEffect, useState } from "react";
import { NotebookPen, Pin, Lock } from "lucide-react";

// MvVisitNotes — the per-visit progress diary inside the Ongoing Treatment
// card. Presentational: it renders the get_mv_notes payload and lifts edits up.
// It never keeps its own copy of saved state (Build Standard #15).
//
//   notes = { next_visit_plan, last_note:{visit_date,doctor_name}|null,
//             current_note:{id,what_happened,next_visit_plan,locked}|null,
//             history:[{id,visit_date,doctor_name,what_happened,
//                       next_visit_plan,addenda,locked}] }
//   appointmentId : the OPEN appointment (chairside). null => read-only file view.
//   onSave({what_happened, next_visit_plan})  → parent calls save_mv_note + re-reads
//   onAddendum(noteId, text)                  → parent calls add_mv_note_addendum

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

export default function MvVisitNotes({ notes, appointmentId, onSave, onAddendum }) {
  const history = notes?.history ?? [];
  const current = notes?.current_note ?? null;

  const [what, setWhat] = useState(current?.what_happened ?? "");
  const [plan, setPlan] = useState(current?.next_visit_plan ?? "");

  // Re-sync when the parent re-reads (e.g. after a save or patient switch).
  useEffect(() => {
    setWhat(current?.what_happened ?? "");
    setPlan(current?.next_visit_plan ?? "");
  }, [current?.id, current?.what_happened, current?.next_visit_plan]);

  const save = () => {
    // Empty-autosave guard: a bare focus/blur with nothing typed must not fire
    // a save. That would create empty note rows + audit-log spam. Only skip when
    // BOTH fields are blank AND no note exists yet — if a note already exists,
    // the doctor may be intentionally clearing it, so we still lift the edit up.
    const bothBlank = !what.trim() && !plan.trim();
    if (bothBlank && !current?.id) return;
    onSave?.({ what_happened: what, next_visit_plan: plan });
  };

  return (
    <div data-testid="mv-visit-notes" className="border-t border-slate-100 px-4 py-3 space-y-3">
      <div className="flex items-center gap-2">
        <NotebookPen className="w-4 h-4 text-violet-600" />
        <p className="text-sm font-semibold text-slate-900">Visit notes</p>
      </div>

      {/* Pinned "pick up where you left off" banner. */}
      {notes?.last_note ? (
        <div className="rounded-lg bg-violet-50 px-3 py-2 flex items-start gap-2">
          <Pin className="w-3.5 h-3.5 text-violet-600 mt-0.5 shrink-0" />
          <p className="text-[12px] text-violet-900">
            <span className="font-semibold">
              Last visit ({fmtDate(notes.last_note.visit_date)} · {notes.last_note.doctor_name}):{" "}
            </span>
            {notes.next_visit_plan || "No plan noted last visit"}
          </p>
        </div>
      ) : null}

      {/* Today's editor — only chairside (open appointment). What we did
          leads (you record the visit first, then plan the next one). */}
      {appointmentId ? (
        <div className="space-y-2">
          <div>
            <label className="text-[11px] font-semibold text-slate-500">What we did today</label>
            <textarea
              data-testid="mv-note-what"
              value={what}
              onChange={(e) => setWhat(e.target.value)}
              onBlur={save}
              rows={2}
              placeholder="e.g. canal cleaned, dry"
              className="mt-1 w-full text-sm border border-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-violet-300"
            />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500">Plan for next visit</label>
            <textarea
              data-testid="mv-note-plan"
              value={plan}
              onChange={(e) => setPlan(e.target.value)}
              onBlur={save}
              rows={2}
              placeholder="e.g. obturate #14 next visit"
              className="mt-1 w-full text-sm border border-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-violet-300"
            />
          </div>
        </div>
      ) : null}

      {/* Locked history — newest first. */}
      {history.length > 0 ? (
        <div className="space-y-2">
          {history.map((h) => (
            <HistoryEntry key={h.id} entry={h} onAddendum={onAddendum} />
          ))}
        </div>
      ) : (
        <p className="text-[12px] text-slate-400">No past visit notes yet.</p>
      )}
    </div>
  );
}

function HistoryEntry({ entry, onAddendum }) {
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState("");
  const addenda = entry.addenda ?? [];

  return (
    <div className="rounded-lg border border-slate-100 px-3 py-2">
      <div className="flex items-center gap-1.5 mb-1">
        <Lock className="w-3 h-3 text-slate-400" />
        <p className="text-[11px] font-semibold text-slate-500">
          {fmtDate(entry.visit_date)} · {entry.doctor_name}
        </p>
      </div>
      {entry.what_happened ? (
        <p className="text-[12px] text-slate-800">
          <span className="font-semibold">Last visit: </span>{entry.what_happened}
        </p>
      ) : null}
      {entry.next_visit_plan ? (
        <p className="text-[12px] text-slate-600">
          <span className="font-semibold">For this visit: </span>{entry.next_visit_plan}
        </p>
      ) : null}

      {addenda.map((a, i) => (
        <p key={i} className="text-[11px] text-slate-500 mt-1 pl-2 border-l-2 border-slate-200">
          <span className="font-semibold">Addendum ({fmtDate(a.at)}): </span>{a.text}
        </p>
      ))}

      {onAddendum ? (
        adding ? (
          <div className="mt-1.5 flex items-center gap-1.5">
            <input
              data-testid={`mv-addendum-input-${entry.id}`}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Add a correction…"
              className="flex-1 text-[12px] border border-slate-200 rounded px-2 py-1"
            />
            <button
              type="button"
              onClick={() => { if (text.trim()) { onAddendum(entry.id, text.trim()); setText(""); setAdding(false); } }}
              className="text-[11px] font-semibold px-2 py-1 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            >
              Save
            </button>
          </div>
        ) : (
          <button
            type="button"
            data-testid={`mv-addendum-open-${entry.id}`}
            onClick={() => setAdding(true)}
            className="mt-1 text-[11px] font-medium text-violet-600 hover:underline"
          >
            Add note
          </button>
        )
      ) : null}
    </div>
  );
}
